import DashboardLayout from '@/components/DashboardLayout'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ImageUpload } from '@/components/ui/image-upload'
import ShareFiscalRequestButton from '@/components/fiscal/ShareFiscalRequestButton'
import FiscalStatusBadges from '@/components/fiscal/FiscalStatusBadges'
import type { FiscalShareRequest, FiscalStatus } from '@/types/fiscal'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowUpCircle,
  ArrowDownCircle,
  ChevronLeft,
  ChevronRight,
  Info,
  CreditCard,
  Building2,
  Plus,
  Package,
  Trash2,
  Edit2,
  ShoppingBag,
} from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useState, useMemo } from 'react'
import { toast } from 'sonner'

// ── Helpers ──

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatCompactCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

function parseCurrencyInput(raw: string): number {
  const digits = raw.replace(/\D/g, '')
  return parseInt(digits || '0', 10) / 100
}

function formatCurrencyInput(value: number): string {
  if (value === 0) return ''
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// ── Currency Input Component ──

function CurrencyInput({
  value,
  onChange,
  ...props
}: { value: number; onChange: (v: number) => void } & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
>) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseCurrencyInput(e.target.value))
  }
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
        R$
      </span>
      <Input
        {...props}
        type="text"
        inputMode="numeric"
        value={formatCurrencyInput(value)}
        onChange={handleChange}
        className="pl-9"
      />
    </div>
  )
}

// ── Interfaces ──

interface Student {
  id: number
  name: string
  email: string
}

interface LessonPackage {
  id: number
  name: string
  lessons_count: number
}

interface StudentSubscription {
  id: number
  lessons_remaining: number
}

interface Payment {
  id: number
  amount: string
  formatted_amount: string
  payment_method: string
  paid_at: string | null
  status: string
  created_at: string
  student: Student
  lesson_package: LessonPackage
  student_subscription: StudentSubscription
}

interface Teacher {
  id: number
  name: string
}

interface TeacherPayment {
  id: number
  teacher: Teacher
  total_amount: number
  formatted_total: string
  status: string
  payment_date: string | null
  created_at: string
}

interface FinancialEntryData {
  id: number
  entry_type: 'income' | 'expense'
  description: string
  category: string | null
  amount: number
  entry_date: string
  notes: string | null
  created_at: string
  quantity: number
  product: { id: number; description: string } | null
  student: { id: number; name: string } | null
  formatted_amount: string
}

interface ProductData {
  id: number
  description: string
  sale_price: number
  cost_price: number | null
  revenue_category: string
  product_category: string | null
  allows_installments: boolean
  tracks_stock: boolean
  current_stock: number
  min_stock_quantity: number
  low_stock: boolean
  notes: string | null
  formatted_sale_price: string
  /** Homologação no Conta Azul. null quando a escola não emite nota de produto. */
  fiscal_status: FiscalStatus | null
  pending_sales_count: number
  share_request: FiscalShareRequest | null
}

interface Stats {
  total_revenue: number
  pending_payments: number
  revenue_this_month: number
  total_expenses: number
  pending_expenses: number
  expenses_this_month: number
  net_profit: number
  revenue_by_month: Array<{
    month: string
    revenue: number
    expenses: number
    profit: number
  }>
}

interface Filters {
  start_date?: string
  end_date?: string
}

interface StudentOption {
  id: number
  name: string
}

interface Props {
  payments: Payment[]
  teacher_payments: TeacherPayment[]
  financial_entries: FinancialEntryData[]
  products: ProductData[]
  students: StudentOption[]
  fiscal_enabled: boolean
  expense_categories: string[]
  stats: Stats
  filters: Filters
}

const STATUS_MAP: Record<
  string,
  {
    label: string
    variant: 'default' | 'secondary' | 'destructive' | 'outline'
  }
> = {
  paid: { label: 'Pago', variant: 'default' },
  pending: { label: 'Pendente', variant: 'secondary' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
  refunded: { label: 'Reembolsado', variant: 'outline' },
}

const EXPENSE_CATEGORIES = [
  'Aluguel',
  'Material',
  'Manutencao',
  'Equipamentos',
  'Marketing',
  'Salarios',
  'Impostos',
  'Alimentacao',
  'Transporte',
  'Seguros',
  'Servicos',
  'Outros',
]

const PAYMENT_METHOD_MAP: Record<string, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartao de Credito',
  debit_card: 'Cartao de Debito',
  pix: 'PIX',
  bank_transfer: 'Transferencia',
}

// ── Main Component ──

export default function Index({
  payments,
  teacher_payments,
  financial_entries,
  products,
  students,
  fiscal_enabled,
  expense_categories,
  stats,
  filters,
}: Props) {
  const [startDate, setStartDate] = useState(filters.start_date || '')
  const [endDate, setEndDate] = useState(filters.end_date || '')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // Dialog states
  const [incomeOpen, setIncomeOpen] = useState(false)
  const [expenseOpen, setExpenseOpen] = useState(false)
  const [productOpen, setProductOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<ProductData | null>(null)
  const [deleteProductId, setDeleteProductId] = useState<number | null>(null)
  const [deleteEntryId, setDeleteEntryId] = useState<number | null>(null)

  // Income form
  const [incomeForm, setIncomeForm] = useState({
    product_id: '' as string,
    student_id: '' as string,
    quantity: 1,
    description: '',
    amount: 0,
    entry_date: new Date().toISOString().split('T')[0],
    notes: '',
  })

  // Expense form
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    description: '',
    amount: 0,
    entry_date: new Date().toISOString().split('T')[0],
    notes: '',
  })

  // Product form
  const [productForm, setProductForm] = useState({
    description: '',
    sale_price: 0,
    cost_price: 0,
    revenue_category: 'Vendas',
    product_category: '',
    allows_installments: false,
    tracks_stock: false,
    current_stock: 0,
    min_stock_quantity: 0,
    notes: '',
  })
  const [productImage, setProductImage] = useState<File | null>(null)

  // ── Transactions merge ──

  const allTransactions = useMemo(() => {
    const revenues = payments.map((p) => ({
      id: `payment-${p.id}`,
      type: 'revenue' as const,
      date: p.paid_at || p.created_at,
      description: `${p.student.name} - ${p.lesson_package.name}`,
      amount: parseFloat(p.amount),
      status: p.status,
      payment_method: p.payment_method,
      category: 'Assinatura',
      source: 'payment' as const,
      sourceId: p.id,
    }))

    const expenses = teacher_payments.map((tp) => ({
      id: `teacher-${tp.id}`,
      type: 'expense' as const,
      date: tp.payment_date || tp.created_at,
      description: `Pagamento - ${tp.teacher.name}`,
      amount: tp.total_amount,
      status: tp.status,
      payment_method: null as string | null,
      category: 'Professores',
      source: 'teacher_payment' as const,
      sourceId: tp.id,
    }))

    const entries = financial_entries.map((fe) => ({
      id: `entry-${fe.id}`,
      type:
        fe.entry_type === 'income'
          ? ('revenue' as const)
          : ('expense' as const),
      date: fe.entry_date,
      description: fe.description,
      amount: fe.amount,
      status: 'paid' as string,
      payment_method: null as string | null,
      category:
        fe.category || (fe.entry_type === 'income' ? 'Vendas' : 'Outros'),
      source: 'financial_entry' as const,
      sourceId: fe.id,
    }))

    return [...revenues, ...expenses, ...entries].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
  }, [payments, teacher_payments, financial_entries])

  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((transaction) => {
      const transactionDate = new Date(transaction.date)
      if (startDate) {
        const start = new Date(startDate)
        if (transactionDate < start) return false
      }
      if (endDate) {
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        if (transactionDate > end) return false
      }
      return true
    })
  }, [allTransactions, startDate, endDate])

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredTransactions.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredTransactions, currentPage])

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage)

  // ── Handlers ──

  const handleFilterChange = () => {
    setCurrentPage(1)
    router.get(
      '/financial',
      {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      },
      { preserveState: true },
    )
  }

  const handleProductSelect = (productId: string) => {
    const product = products.find((p) => p.id === parseInt(productId))
    if (product) {
      setIncomeForm((prev) => ({
        ...prev,
        product_id: productId,
        description: product.description,
        amount: product.sale_price * prev.quantity,
      }))
    }
  }

  // Quantidade recalcula o total, mas o valor segue editável (desconto de balcão).
  const handleQuantityChange = (value: number) => {
    const quantity = Number.isFinite(value) && value > 0 ? Math.floor(value) : 1
    const product = products.find(
      (p) => p.id === parseInt(incomeForm.product_id),
    )
    setIncomeForm((prev) => ({
      ...prev,
      quantity,
      amount: product ? product.sale_price * quantity : prev.amount,
    }))
  }

  const selectedProduct = products.find(
    (p) => p.id === parseInt(incomeForm.product_id),
  )

  const resetIncomeForm = () => {
    setIncomeForm({
      product_id: '',
      student_id: '',
      quantity: 1,
      description: '',
      amount: 0,
      entry_date: new Date().toISOString().split('T')[0],
      notes: '',
    })
  }

  const resetExpenseForm = () => {
    setExpenseForm({
      category: '',
      description: '',
      amount: 0,
      entry_date: new Date().toISOString().split('T')[0],
      notes: '',
    })
  }

  const resetProductForm = () => {
    setProductForm({
      description: '',
      sale_price: 0,
      cost_price: 0,
      revenue_category: 'Vendas',
      product_category: '',
      allows_installments: false,
      tracks_stock: false,
      current_stock: 0,
      min_stock_quantity: 0,
      notes: '',
    })
    setProductImage(null)
    setEditingProduct(null)
  }

  const submitIncome = () => {
    if (
      !incomeForm.description ||
      incomeForm.amount <= 0 ||
      !incomeForm.entry_date
    ) {
      toast.error('Preencha todos os campos obrigatorios')
      return
    }
    router.post(
      '/financial_entries',
      {
        financial_entry: {
          entry_type: 'income',
          product_id: incomeForm.product_id || null,
          student_id: incomeForm.student_id || null,
          quantity: incomeForm.quantity,
          description: incomeForm.description,
          category: 'Vendas',
          amount: incomeForm.amount,
          entry_date: incomeForm.entry_date,
          notes: incomeForm.notes,
        },
      },
      {
        onSuccess: () => {
          setIncomeOpen(false)
          resetIncomeForm()
        },
        onError: (errors) => {
          toast.error(Object.values(errors).flat().join(', '))
        },
      },
    )
  }

  const submitExpense = () => {
    if (
      !expenseForm.description ||
      expenseForm.amount <= 0 ||
      !expenseForm.entry_date
    ) {
      toast.error('Preencha todos os campos obrigatorios')
      return
    }
    router.post(
      '/financial_entries',
      {
        financial_entry: {
          entry_type: 'expense',
          description: expenseForm.description,
          category: expenseForm.category,
          amount: expenseForm.amount,
          entry_date: expenseForm.entry_date,
          notes: expenseForm.notes,
        },
      },
      {
        onSuccess: () => {
          setExpenseOpen(false)
          resetExpenseForm()
        },
        onError: (errors) => {
          toast.error(Object.values(errors).flat().join(', '))
        },
      },
    )
  }

  const submitProduct = () => {
    if (!productForm.description || productForm.sale_price <= 0) {
      toast.error('Preencha descricao e preco de venda')
      return
    }
    const formData = new FormData()
    Object.entries(productForm).forEach(([key, value]) => {
      formData.append(`product[${key}]`, String(value))
    })
    if (productImage) {
      formData.append('product[image]', productImage)
    }

    if (editingProduct) {
      router.patch(`/products/${editingProduct.id}`, formData, {
        forceFormData: true,
        onSuccess: () => {
          setProductOpen(false)
          resetProductForm()
        },
        onError: (errors) => {
          toast.error(Object.values(errors).flat().join(', '))
        },
      })
    } else {
      router.post('/products', formData, {
        forceFormData: true,
        onSuccess: () => {
          setProductOpen(false)
          resetProductForm()
        },
        onError: (errors) => {
          toast.error(Object.values(errors).flat().join(', '))
        },
      })
    }
  }

  const openEditProduct = (product: ProductData) => {
    setEditingProduct(product)
    setProductForm({
      description: product.description,
      sale_price: product.sale_price,
      cost_price: product.cost_price || 0,
      revenue_category: product.revenue_category || 'Vendas',
      product_category: product.product_category || '',
      allows_installments: product.allows_installments,
      tracks_stock: product.tracks_stock,
      current_stock: product.current_stock,
      min_stock_quantity: product.min_stock_quantity,
      notes: product.notes || '',
    })
    setProductOpen(true)
  }

  const confirmDeleteProduct = () => {
    if (!deleteProductId) return
    router.delete(`/products/${deleteProductId}`, {
      onSuccess: () => setDeleteProductId(null),
    })
  }

  const confirmDeleteEntry = () => {
    if (!deleteEntryId) return
    router.delete(`/financial_entries/${deleteEntryId}`, {
      onSuccess: () => setDeleteEntryId(null),
    })
  }

  // ── Render ──

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0 p-3 bg-primary/10 rounded-lg">
              <DollarSign className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Financeiro</h1>
              <p className="text-muted-foreground">
                Controle de receitas, despesas e produtos
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                resetIncomeForm()
                setIncomeOpen(true)
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-1" /> Nova Entrada
            </Button>
            <Button
              onClick={() => {
                resetExpenseForm()
                setExpenseOpen(true)
              }}
              variant="destructive"
            >
              <Plus className="w-4 h-4 mr-1" /> Nova Saida
            </Button>
            <Button
              onClick={() => {
                resetProductForm()
                setProductOpen(true)
              }}
              variant="outline"
            >
              <Package className="w-4 h-4 mr-1" /> Produto
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="overview">
              <DollarSign className="h-4 w-4 mr-1.5" />
              Visao Geral
            </TabsTrigger>
            <TabsTrigger value="products">
              <ShoppingBag className="h-4 w-4 mr-1.5" />
              Produtos
            </TabsTrigger>
            <TabsTrigger value="fees">
              <Info className="h-4 w-4 mr-1.5" />
              Taxas e Repasses
            </TabsTrigger>
          </TabsList>

          {/* ── TAB VISAO GERAL ── */}
          <TabsContent value="overview" className="mt-6">
            {/* Cards de Resumo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="shrink-0 p-3 rounded-xl bg-green-500/10">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">
                        Total de Entradas
                      </p>
                      <p className="text-xl md:text-2xl font-bold text-green-600 break-words tabular-nums">
                        {formatCurrency(stats.total_revenue)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        Este mes: {formatCurrency(stats.revenue_this_month)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="shrink-0 p-3 rounded-xl bg-red-500/10">
                      <TrendingDown className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">
                        Total de Saidas
                      </p>
                      <p className="text-xl md:text-2xl font-bold text-red-600 break-words tabular-nums">
                        {formatCurrency(stats.total_expenses)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        Este mes: {formatCurrency(stats.expenses_this_month)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="shrink-0 p-3 rounded-xl bg-primary/10">
                      <DollarSign className="w-6 h-6 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">Saldo</p>
                      <p
                        className={`text-xl md:text-2xl font-bold break-words tabular-nums ${stats.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {formatCurrency(stats.net_profit)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 break-words">
                        Pendente:{' '}
                        {formatCurrency(
                          stats.pending_payments + stats.pending_expenses,
                        )}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Grafico */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>
                  Receitas, Despesas e Lucro - Ultimos 6 Meses
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={stats.revenue_by_month}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis
                      tickFormatter={formatCompactCurrency}
                      width={64}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value: unknown) =>
                        typeof value === 'number'
                          ? formatCurrency(value)
                          : 'R$ 0,00'
                      }
                    />
                    <Legend />
                    <Bar
                      dataKey="revenue"
                      fill="#16a34a"
                      name="Receita"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="expenses"
                      fill="#dc2626"
                      name="Despesas"
                      radius={[8, 8, 0, 0]}
                    />
                    <Bar
                      dataKey="profit"
                      fill="hsl(var(--primary))"
                      name="Lucro"
                      radius={[8, 8, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Transacoes */}
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle>Transacoes</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      onBlur={handleFilterChange}
                      className="w-36 sm:w-40"
                    />
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      onBlur={handleFilterChange}
                      className="w-36 sm:w-40"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredTransactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhuma transacao encontrada</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>Descricao</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedTransactions.map((t) => (
                          <TableRow key={t.id}>
                            <TableCell>
                              {t.type === 'revenue' ? (
                                <div className="flex items-center gap-2 text-green-600">
                                  <ArrowUpCircle className="w-4 h-4" />
                                  <span className="font-medium text-sm">
                                    Entrada
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-red-600">
                                  <ArrowDownCircle className="w-4 h-4" />
                                  <span className="font-medium text-sm">
                                    Saida
                                  </span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {format(
                                new Date(
                                  t.date +
                                    (t.date.length === 10 ? 'T00:00:00' : ''),
                                ),
                                'dd/MM/yyyy',
                                { locale: ptBR },
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">
                                {t.description}
                              </div>
                              {t.payment_method && (
                                <div className="text-xs text-muted-foreground">
                                  {PAYMENT_METHOD_MAP[t.payment_method] ||
                                    t.payment_method}
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {t.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  STATUS_MAP[t.status]?.variant || 'default'
                                }
                              >
                                {STATUS_MAP[t.status]?.label || t.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <span
                                className={`text-sm font-bold ${t.type === 'revenue' ? 'text-green-600' : 'text-red-600'}`}
                              >
                                {t.type === 'revenue' ? '+' : '-'}{' '}
                                {formatCurrency(t.amount)}
                              </span>
                            </TableCell>
                            <TableCell>
                              {t.source === 'financial_entry' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setDeleteEntryId(t.sourceId)}
                                >
                                  <Trash2 className="w-4 h-4 text-muted-foreground" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {totalPages > 1 && (
                      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                          {Math.min(
                            currentPage * itemsPerPage,
                            filteredTransactions.length,
                          )}{' '}
                          de {filteredTransactions.length} transacoes
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <span className="text-sm">
                            Pagina {currentPage} de {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB PRODUTOS ── */}
          <TabsContent value="products" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <CardTitle>Produtos Cadastrados</CardTitle>
                  <Button
                    onClick={() => {
                      resetProductForm()
                      setProductOpen(true)
                    }}
                    variant="outline"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Novo Produto
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {products.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nenhum produto cadastrado</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        resetProductForm()
                        setProductOpen(true)
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Cadastrar Produto
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Descricao</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Preco de Custo</TableHead>
                        <TableHead>Preco de Venda</TableHead>
                        <TableHead>Estoque</TableHead>
                        {fiscal_enabled && <TableHead>Fiscal</TableHead>}
                        <TableHead className="text-right">Acoes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">
                            {product.description}
                          </TableCell>
                          <TableCell>
                            {product.product_category ? (
                              <Badge variant="outline">
                                {product.product_category}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {product.cost_price != null ? (
                              formatCurrency(product.cost_price)
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {formatCurrency(product.sale_price)}
                          </TableCell>
                          <TableCell>
                            {product.tracks_stock ? (
                              <div className="flex items-center gap-2">
                                <span
                                  className={`font-semibold ${product.low_stock ? 'text-red-600' : ''}`}
                                >
                                  {product.current_stock}
                                </span>
                                {product.low_stock && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs"
                                  >
                                    Baixo
                                  </Badge>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">
                                Nao controla
                              </span>
                            )}
                          </TableCell>
                          {fiscal_enabled && (
                            <TableCell>
                              <div className="flex flex-col items-start gap-2">
                                <FiscalStatusBadges
                                  fiscalStatus={
                                    product.fiscal_status ?? 'pending'
                                  }
                                  pendingSalesCount={
                                    product.pending_sales_count
                                  }
                                />
                                {product.fiscal_status !== 'homologated' &&
                                  product.share_request && (
                                    <ShareFiscalRequestButton
                                      mappableType="product"
                                      mappableId={product.id}
                                      subject={product.share_request.subject}
                                      body={product.share_request.body}
                                      pendingSalesCount={
                                        product.pending_sales_count
                                      }
                                    />
                                  )}
                              </div>
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditProduct(product)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setDeleteProductId(product.id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB TAXAS E REPASSES ── */}
          <TabsContent value="fees" className="mt-6 space-y-6">
            <div className="relative overflow-hidden rounded-xl shadow-md">
              <div className="absolute inset-0 bg-gradient-to-r from-slate-700 via-slate-800 to-slate-900" />
              <div className="relative p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="h-5 w-5 text-slate-300" />
                  <h3 className="text-lg font-bold text-white">
                    Como funciona o pagamento pelo app
                  </h3>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed max-w-2xl">
                  Quando um aluno compra um pacote pelo aplicativo, o pagamento
                  e processado pelo ASAAS (PIX, debito ou cartao de credito). A
                  BeWave desconta a taxa e repassa o restante para a sua escola
                  a cada quinzena.
                </p>
              </div>
            </div>

            {/* A tabela de taxas e o extrato vivem em /payouts, alimentados pelo
              PlatformFeeTable. Repetir os numeros aqui criaria duas versoes da
              verdade que divergem no primeiro reajuste. */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Seus repasses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Veja quais pagamentos entraram, quanto voce recebe de cada um,
                  a taxa aplicada por forma de pagamento e a data em que o
                  dinheiro cai na sua conta. O fechamento e quinzenal: o que
                  liquidou entre os dias 01 e 15 cai no dia 20, e o que liquidou
                  do dia 16 ao fim do mes cai no dia 05 do mes seguinte.
                </p>
                <Button onClick={() => router.visit('/payouts')}>
                  Abrir Repasses
                </Button>
              </CardContent>
            </Card>

            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">
                  Importante:
                </span>{' '}
                pagamentos manuais (dinheiro, transferencia) registrados na
                escola nao passam pelo ASAAS, nao tem taxa e nao entram no
                repasse.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── DIALOG: NOVA ENTRADA ── */}
      <Dialog
        open={incomeOpen}
        onOpenChange={(open) => {
          setIncomeOpen(open)
          if (!open) resetIncomeForm()
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="w-5 h-5 text-green-600" />
              Nova Entrada
            </DialogTitle>
            <DialogDescription>Registrar uma receita</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Produto (opcional)</Label>
              <Select
                value={incomeForm.product_id}
                onValueChange={handleProductSelect}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione um produto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => {
                    const label =
                      p.description.length > 40
                        ? p.description.slice(0, 40) + '...'
                        : p.description
                    return (
                      <SelectItem
                        key={p.id}
                        value={String(p.id)}
                        textValue={label + ' - ' + formatCurrency(p.sale_price)}
                      >
                        {label} - {formatCurrency(p.sale_price)}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            </div>
            {incomeForm.product_id && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantidade *</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={incomeForm.quantity}
                    onChange={(e) =>
                      handleQuantityChange(parseInt(e.target.value))
                    }
                    className="mt-1"
                  />
                  {selectedProduct?.tracks_stock && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Estoque atual: {selectedProduct.current_stock}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Aluno (opcional)</Label>
                  <Select
                    value={incomeForm.student_id}
                    onValueChange={(v) =>
                      setIncomeForm({
                        ...incomeForm,
                        student_id: v === 'none' ? '' : v,
                      })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Consumidor nao identificado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        Consumidor nao identificado
                      </SelectItem>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={String(student.id)}>
                          {student.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label>Descricao *</Label>
              <Input
                value={incomeForm.description}
                onChange={(e) =>
                  setIncomeForm({ ...incomeForm, description: e.target.value })
                }
                className="mt-1"
                placeholder="Digite a descricao"
              />
            </div>
            <div>
              <Label>Valor *</Label>
              <CurrencyInput
                value={incomeForm.amount}
                onChange={(v) => setIncomeForm({ ...incomeForm, amount: v })}
                className="mt-1"
              />
              {incomeForm.product_id && incomeForm.quantity > 1 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Total de {incomeForm.quantity} unidades. Edite se houver
                  desconto.
                </p>
              )}
            </div>
            <div>
              <Label>Data *</Label>
              <Input
                type="date"
                value={incomeForm.entry_date}
                onChange={(e) =>
                  setIncomeForm({ ...incomeForm, entry_date: e.target.value })
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label>Observacoes</Label>
              <Textarea
                value={incomeForm.notes}
                onChange={(e) =>
                  setIncomeForm({ ...incomeForm, notes: e.target.value })
                }
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncomeOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submitIncome}
              className="bg-green-600 hover:bg-green-700"
            >
              Salvar Entrada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: NOVA SAIDA ── */}
      <Dialog
        open={expenseOpen}
        onOpenChange={(open) => {
          setExpenseOpen(open)
          if (!open) resetExpenseForm()
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownCircle className="w-5 h-5 text-red-600" />
              Nova Saida
            </DialogTitle>
            <DialogDescription>Registrar uma despesa</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Categoria</Label>
              <Select
                value={expenseForm.category}
                onValueChange={(v) =>
                  setExpenseForm({ ...expenseForm, category: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                  {expense_categories
                    .filter((c) => !EXPENSE_CATEGORIES.includes(c))
                    .map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descricao *</Label>
              <Input
                value={expenseForm.description}
                onChange={(e) =>
                  setExpenseForm({
                    ...expenseForm,
                    description: e.target.value,
                  })
                }
                className="mt-1"
                placeholder="Digite a descricao"
              />
            </div>
            <div>
              <Label>Valor *</Label>
              <CurrencyInput
                value={expenseForm.amount}
                onChange={(v) => setExpenseForm({ ...expenseForm, amount: v })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Data *</Label>
              <Input
                type="date"
                value={expenseForm.entry_date}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, entry_date: e.target.value })
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label>Observacoes</Label>
              <Textarea
                value={expenseForm.notes}
                onChange={(e) =>
                  setExpenseForm({ ...expenseForm, notes: e.target.value })
                }
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submitExpense} variant="destructive">
              Salvar Saida
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: PRODUTO ── */}
      <Dialog
        open={productOpen}
        onOpenChange={(open) => {
          setProductOpen(open)
          if (!open) resetProductForm()
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Produtos sao usados para registrar entradas (receitas)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Descricao *</Label>
              <Input
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    description: e.target.value,
                  })
                }
                className="mt-1"
                placeholder="Digite a descricao"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Preco de Venda *</Label>
                <CurrencyInput
                  value={productForm.sale_price}
                  onChange={(v) =>
                    setProductForm({ ...productForm, sale_price: v })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Preco de Custo</Label>
                <CurrencyInput
                  value={productForm.cost_price}
                  onChange={(v) =>
                    setProductForm({ ...productForm, cost_price: v })
                  }
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Categoria de Receita</Label>
                <Input
                  value={productForm.revenue_category}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      revenue_category: e.target.value,
                    })
                  }
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Categoria do Produto</Label>
                <Input
                  value={productForm.product_category}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      product_category: e.target.value,
                    })
                  }
                  className="mt-1"
                  placeholder="Digite a categoria"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Permite parcelamento</p>
                <p className="text-xs text-muted-foreground">
                  Habilitar pagamento parcelado
                </p>
              </div>
              <Switch
                checked={productForm.allows_installments}
                onCheckedChange={(v) =>
                  setProductForm({ ...productForm, allows_installments: v })
                }
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Controla estoque</p>
                <p className="text-xs text-muted-foreground">
                  Monitorar quantidade em estoque
                </p>
              </div>
              <Switch
                checked={productForm.tracks_stock}
                onCheckedChange={(v) =>
                  setProductForm({ ...productForm, tracks_stock: v })
                }
              />
            </div>
            {productForm.tracks_stock && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Estoque atual</Label>
                  <Input
                    type="number"
                    min="0"
                    value={productForm.current_stock}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        current_stock: parseInt(e.target.value) || 0,
                      })
                    }
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Quantidade minima</Label>
                  <Input
                    type="number"
                    min="0"
                    value={productForm.min_stock_quantity}
                    onChange={(e) =>
                      setProductForm({
                        ...productForm,
                        min_stock_quantity: parseInt(e.target.value) || 0,
                      })
                    }
                    className="mt-1"
                  />
                </div>
              </div>
            )}
            <div>
              <Label>Observacoes</Label>
              <Textarea
                value={productForm.notes}
                onChange={(e) =>
                  setProductForm({ ...productForm, notes: e.target.value })
                }
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Imagem</Label>
              <div className="mt-1">
                <ImageUpload onImageSelect={setProductImage} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={submitProduct}>
              {editingProduct ? 'Salvar Alteracoes' : 'Criar Produto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: CONFIRMAR EXCLUSAO PRODUTO ── */}
      <Dialog
        open={deleteProductId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteProductId(null)
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Produto</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este produto? Esta acao nao pode
              ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteProductId(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDeleteProduct}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG: CONFIRMAR EXCLUSAO LANCAMENTO ── */}
      <Dialog
        open={deleteEntryId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteEntryId(null)
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir Lancamento</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este lancamento? Esta acao nao pode
              ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteEntryId(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDeleteEntry}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
